import type {
  AenderungsTyp,
  AltbuergschaftBearbeiten,
  AltbürgschaftValidierungsErgebnis as AltbuergschaftValidierungsErgebnis,
  BearbeitenDaten,
  Beteiligte,
  BuergschaftAendernHistorie,
  BuergschaftVertragBearbeitenBulk,
  Bürgschaft as Buergschaft,
  BürgschaftsAuftrag as BuergschaftsAuftrag,
  Bürgschaftserinnerung as Buergschaftserinnerung,
  Bürgschaftskommentar as Buergschaftskommentar,
  BürgschaftskommentarUpload as BuergschaftskommentarUpload,
  Bürgschaftskorrektur as Buergschaftskorrektur,
  Teilenthaftung,
  VorlageViolationsDto,
} from '@/types/Bürgschaft';
import type {
  AbtretungAblehnenDto,
  AbtretungErstellenDto,
  BuergschaftAbtretung,
  RueckforderungAblehnenDto,
} from '@/shared/Buergschaften/types';
import type { EintrittskarteRequest } from '@/types/Partner';
import type { BürgschaftFilterResponse as BuergschaftFilterResponse } from '@/connection/filter-helper/filter-helper-bürgschaften';
import { configForDownload } from '@/connection/base';
import { SharedBuergschaftService } from '@/shared/api/services/buergschaft';
import type { AxiosResponse } from 'axios';
import type { InanspruchnahmeUpload } from '@/shared/Buergschaften/inanspruchnahme/types.ts';

export class BuergschaftService extends SharedBuergschaftService {
  async holeBuergschaftserinnerung(id: number | string): Promise<Buergschaftserinnerung> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}/erinnerung`);
  }

  async holeAnzahlBuergschaftsKommentare(id: string): Promise<number> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/kommentar/anzahl`,
    );
  }

  async holeBuergschaftskommentare(
    buergschaftId: number | string,
    rolle: Beteiligte = undefined,
  ): Promise<Buergschaftskommentar[]> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/kommentar`,
      {
        params: {
          rolle,
        },
      },
    );
  }

  async treteBuergschaftAb(
    id: number,
    abtretung: AbtretungErstellenDto,
  ): Promise<BuergschaftAbtretung> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}/abtretung`, {
      data: abtretung,
    });
  }

  async uebermittelInanspruchnahme(
    inanspruchnahme: InanspruchnahmeUpload,
  ): Promise<BuergschaftAbtretung> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/inanspruchnahme`, {
      data: inanspruchnahme,
    });
  }

  async holeAbtretung(id: number, beteiligter: Beteiligte): Promise<BuergschaftAbtretung> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/buergschaften/abgetreten/${id}`, {
      params: {
        beteiligter,
      },
    });
  }

  async akzeptiereAbtretung(abtretungId: number, haftungsausschlussTextId: number): Promise<void> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/abtretung/${abtretungId}/akzeptieren`,
      {
        data: { haftungsausschlussTextId },
      },
    );
  }

  async ablehneAbtretung(abtretungId: number, ablehnenDto: AbtretungAblehnenDto): Promise<void> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/abtretung/${abtretungId}/ablehnen`,
      { data: ablehnenDto },
    );
  }

  async rueckforderungAkzeptiert(
    buergschaftId: number,
    version: number,
  ): Promise<AxiosResponse<Buergschaft>> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/rueckforderung/${buergschaftId}/akzeptiert`,
      {
        data: {
          bürgschaftVersion: version,
        },
      },
    );
  }

  async rueckforderungAblehnen(
    buergschaftId: number,
    ablehnenDto: RueckforderungAblehnenDto,
  ): Promise<AxiosResponse<void>> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/rueckforderung/${buergschaftId}/ablehnen`,
      { data: ablehnenDto },
    );
  }

  async erstelleBuergschaftserinnerung(
    buergschaftId: string,
    erinnerung: Buergschaftserinnerung,
  ): Promise<Buergschaftserinnerung> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/erinnerung`,
      { data: erinnerung },
    );
  }

  async bearbeiteBuergschaftserinnerung(
    erinnerungId: number,
    erinnerung: Buergschaftserinnerung,
  ): Promise<Buergschaftserinnerung> {
    return this.httpClient.patch(
      `${this.buergschaftServiceBaseUrl}/buergschaften/erinnerung/${erinnerungId}`,
      { data: erinnerung },
    );
  }

  async loescheBuergschaftserinnerung(erinnerungId: number): Promise<void> {
    return this.httpClient.delete(
      `${this.buergschaftServiceBaseUrl}/buergschaften/erinnerung/${erinnerungId}`,
    );
  }

  async erstelleBuergschaftskommentar(
    buergschaftId: string,
    kommentar: BuergschaftskommentarUpload,
    rolle: Beteiligte = undefined,
  ): Promise<Buergschaftskommentar> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/kommentar`,
      {
        data: kommentar,
        params: { rolle },
      },
    );
  }

  async bearbeiteBuergschaftskommentar(
    kommentarId: number,
    kommentar: BuergschaftskommentarUpload,
    rolle: Beteiligte = undefined,
  ): Promise<Buergschaftskommentar> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaftskommentar/${kommentarId}`,
      {
        data: kommentar,
        params: { rolle },
      },
    );
  }

  async loescheBuergschaftskommentar(
    kommentarId: number,
    rolle: Beteiligte = undefined,
  ): Promise<void> {
    return this.httpClient.delete(
      `${this.buergschaftServiceBaseUrl}/buergschaftskommentar/${kommentarId}`,
      {
        params: { rolle },
      },
    );
  }

  async starteValidierungCsvBuergschaften(
    handle: string,
  ): Promise<AltbuergschaftValidierungsErgebnis> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/altbuergschaften/${handle}/asyncvalidierung`,
    );
  }

  async ladeFehlerhafteAltbuergschaftenHerunter(s3Dateiname: string): Promise<BlobPart> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/altbuergschaften/fehlerhafte`, {
      params: { dateiname: s3Dateiname },
      config: configForDownload,
    });
  }

  async ladeInanspruchnahmeDateienZIPHerunter(buergschaft: Buergschaft): Promise<BlobPart> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaft.bürgschaftId}/inanspruchnahmedokumente`,
      { config: configForDownload },
    );
  }

  async erstelleEintrittskarte(
    eintrittskarte: EintrittskarteRequest,
  ): Promise<EintrittskarteRequest> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/eintrittskarte`, {
      data: eintrittskarte,
    });
  }

  async bearbeiteBuergschaft(
    id: number,
    version: number,
    bearbeitenDaten: Partial<BearbeitenDaten>,
  ): Promise<Buergschaft> {
    return this.httpClient.patch(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}`, {
      data: { ...bearbeitenDaten, version },
    });
  }

  async ordneKautionsvertragZu(
    id: number,
    bearbeitenDaten: Partial<BearbeitenDaten>,
  ): Promise<Buergschaft> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}/vertrag`, {
      data: bearbeitenDaten,
    });
  }

  async ordneKautionsvertragMehrerenBuergschaftenZu(
    bulkDto: BuergschaftVertragBearbeitenBulk,
  ): Promise<void> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/vertrag/bulk`, {
      data: bulkDto,
    });
  }

  async hebeKautionsvertragAuf(
    id: number,
    bearbeitenDaten: Partial<BearbeitenDaten>,
  ): Promise<Buergschaft> {
    return this.httpClient.delete(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}/vertrag`, {
      data: bearbeitenDaten,
    });
  }

  async massenKautionsvertragAufhebung(bulkDto: BuergschaftVertragBearbeitenBulk): Promise<void> {
    return this.httpClient.delete(`${this.buergschaftServiceBaseUrl}/buergschaften/vertrag/bulk`, {
      data: bulkDto,
    });
  }

  async rueckgabeBuergschaft(id: number, version: number): Promise<Buergschaft> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}/rueckgabe`, {
      data: {
        bürgschaftVersion: version,
      },
    });
  }

  async teilenthafteBuergschaft(
    id: number,
    version: number,
    minderungsbetrag: string,
    reduzierterBuergschaftsbetrag: string,
  ): Promise<void> {
    const buergschaftsdaten: Teilenthaftung = {
      minderungsbetrag,
      reduzierterBürgschaftsbetrag: reduzierterBuergschaftsbetrag,
      bürgschaftVersion: version,
      wirksamkeitsdatum: null, // wird aktuell noch nicht verwendet
    };
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/teilenthaftung`,
      { data: buergschaftsdaten },
    );
  }

  async bestaetigeInkorrekturBuergschaft(buergschaftId: number): Promise<Buergschaft> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/korrektur/bestaetigung`,
    );
  }

  async beauftrageLoeschung(buergschaftId: number): Promise<BuergschaftFilterResponse> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/loeschungbeauftragen`,
    );
  }

  async isBuergschaftAenderungsHistorieVorhanden(
    id: number,
    aenderungsTyp: AenderungsTyp,
  ): Promise<boolean> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaft/${id}/historie/${aenderungsTyp}/vorhanden`,
    );
  }

  async holeBuergschaftAenderungsHistorie(
    id: number,
    aenderungsTyp: AenderungsTyp,
  ): Promise<BuergschaftAendernHistorie[]> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaft/${id}/historie/${aenderungsTyp}`,
    );
  }

  async erstelleBuergschaftsauftragMitHandle(
    buergschaftsAuftrag: unknown,
    handle?: string,
  ): Promise<BuergschaftsAuftrag> {
    let url = `${this.buergschaftServiceBaseUrl}/buergschaftsauftrag`;
    if (handle) {
      url += `/${handle}`;
    }
    return this.httpClient.post(url, {
      data: buergschaftsAuftrag,
    });
  }

  async ablehneBuergschaft(id: number, version: number): Promise<Buergschaft> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}/ablehnung`, {
      data: { bürgschaftVersion: version },
    });
  }

  async korrekturBuergschaft(
    id: number,
    version: number,
    korrektur: Buergschaftskorrektur,
  ): Promise<Buergschaft> {
    korrektur.bürgschaftVersion = version;
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/korrigierung`,
      { data: korrektur },
    );
  }

  async aktivierungBuergschaft(id: number, version: number): Promise<Buergschaft> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/aktivierung`,
      { data: { bürgschaftVersion: version } },
    );
  }

  async erstelleAltbuergschaft(buergschaft: unknown): Promise<Buergschaft> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/altbuergschaft`, {
      data: buergschaft,
    });
  }

  async bearbeiteAltbuergschaft(
    buergschaftId: number,
    buergschaft: AltbuergschaftBearbeiten,
  ): Promise<Buergschaft> {
    return this.httpClient.patch(
      `${this.buergschaftServiceBaseUrl}/altbuergschaft/${buergschaftId}`,
      {
        data: buergschaft,
      },
    );
  }

  async checkAuftragsvorlage(auftragId: number): Promise<VorlageViolationsDto[]> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaftsauftraege/${auftragId}/vorlage-pruefung`,
    );
  }
}
